/**
 * Learner app store: reducer, provider, and controller hook.
 */
import React, { createContext, useContext, useEffect, useReducer, useRef, useState } from "react";
import PropTypes from "prop-types";

import { loadSession, readLastPath, saveSession, setPreferSession, writeLastPath } from "offline/session";
import { getTimeOfDate } from "utils/date";
import { asArray, asList, asObject } from "utils/safeData";

const AsterContext = createContext();
AsterContext.displayName = "AsterContext";

const PAGE_DEFAULT = {
  total: 0,
  page: 0,
  page_size: 10,
  data: [],
};

/**
 * Applies a store action and returns the next learner app state.
 *
 * @param {Object} state Current store state.
 * @param {{ type: string, value?: * }} action Store action.
 * @returns {Object}
 */
const reducer = (state, action) => {
  const currDate = getTimeOfDate();

  switch (action.type) {
    case "SET_MINI_SIDENAV":
      return { ...state, miniSidenav: action.value };
    case "SET_HEADER_SIDENAV":
      return { ...state, headerSidenav: action.value };
    case "SET_MOBILE":
      return { ...state, mobile: action.value };
    case "SET_DARK_MODE":
      return { ...state, darkMode: action.value };

    case "SET_ADMIN":
      return { ...state, admin: asObject(action.value) };
    case "SET_IS_PROFILE_ALERT":
      return { ...state, isEditProfileAlert: action.value };

    // Auth
    case "SET_LOGIN_HIST_LIST":
      return { ...state, loginHists: asList(action.value) };
    case "SET_USER":
      return { ...state, user: asObject(action.value) };
    case "SET_PROFILE_UPDATE_SUCCESS":
      return { ...state, isProfileUpdateSuccess: action.value };
    case "SET_PROFILE_UPDATE_LOADING":
      return { ...state, isProfileUpdateLoading: action.value };
    case "UPDATE_USER":
      return { ...state, user: { ...asObject(state.user), ...asObject(action.value) } };
    case "SET_USER_GROUP_LIST":
      return { ...state, groups: asArray(action.value) };

    // Category
    case "SET_CATEGORY_LIST":
      return { ...state, categories: asList(action.value) };

    // Certifiate
    case "SET_CERTIFICATE_LIST":
      return { ...state, certificates: asList(action.value) };
    case "ADD_CERTIFICATE":
      return { ...state, certificates: { ...state.certificates, data: [...asArray(state.certificates?.data), action.value] } };
    case "UPDATE_CERTIFICATE":
      return {
        ...state,
        certificates: {
          ...state.certificates,
          data: asArray(state.certificates?.data).map((cert) => (cert.course_id === action.value.course_id && cert.course_title === action.value.course_title && cert.module_title === action.value.module_title ? { id: asArray(state.certificates?.data).length, ...action.value } : cert)),
        },
      };
    case "SET_CERT_VAR":
      return { ...state, certVar: asObject(action.value) };

    // Chat
    case "SET_CHAT_LIST":
      return {
        ...state,
        chatList: {
          message_more: false,
          page: 0,
          ...asObject(action.value),
          messages: asArray(action.value?.messages),
        },
      };
    case "SET_CHAT_MORE_LIST":
      return {
        ...state,
        chatList: {
          ...state.chatList,
          ...asObject(action.value),
          messages: [...asArray(state.chatList?.messages), ...asArray(action.value?.messages)],
        },
      };
    case "ADD_CHAT": {
      const incoming = asObject(action.value);
      const fromId = incoming.from_id;
      let unread = asObject(state.networkUnread);
      if (incoming.is_new && fromId != null) {
        const viewing = Boolean(state.peerChatActive) && String(state.peerChat?.peerId) === String(fromId);
        if (!viewing) {
          const key = String(fromId);
          unread = { ...unread, [key]: (Number(unread[key]) || 0) + 1 };
        }
      }
      return {
        ...state,
        networkUnread: unread,
        chatList: {
          ...state.chatList,
          messages: [action.value, ...asArray(state.chatList?.messages)],
        },
      };
    }
    case "SET_CHAT_ENABLED": {
      const raw = action.value;
      let enabled = true;
      if (typeof raw === "boolean") enabled = raw;
      else if (typeof raw === "number") enabled = raw !== 0;
      else if (raw != null) {
        const s = String(raw).trim().toLowerCase();
        enabled = s !== "" && s !== "0" && s !== "false" && s !== "off" && s !== "no";
      }
      return { ...state, isChatEnabled: enabled };
    }
    case "SET_CHAT_TYPING":
      return { ...state, chatTyping: asObject(action.value) };
    case "SET_PRESENCE": {
      const uid = action.value?.user_id;
      const online = Boolean(action.value?.online);
      if (uid == null) return state;
      const lastSeen = online ? new Date().toISOString() : action.value?.last_seen;
      return {
        ...state,
        networkPresence: { ...asObject(state.networkPresence), [uid]: online },
        networkContacts: asArray(state.networkContacts).map((contact) =>
          String(contact.user_id) === String(uid)
            ? { ...contact, online, last_seen: lastSeen || contact.last_seen }
            : contact
        ),
      };
    }
    case "EXPIRE_PRESENCE": {
      const cutoff = Number(action.value) || Date.now() - 90000;
      let changed = false;
      const networkContacts = asArray(state.networkContacts).map((contact) => {
        if (!contact?.online) return contact;
        const seen = Date.parse(contact.last_seen);
        if (Number.isNaN(seen) || seen >= cutoff) return contact;
        changed = true;
        return { ...contact, online: false };
      });
      if (!changed) return state;
      const networkPresence = { ...asObject(state.networkPresence) };
      networkContacts.forEach((contact) => {
        if (contact?.user_id != null) networkPresence[contact.user_id] = Boolean(contact.online);
      });
      return { ...state, networkContacts, networkPresence };
    }
    case "SET_NETWORK_LIST":
      return { ...state, networkContacts: asArray(action.value) };
    case "LOADED_NETWORK_LIST":
      return { ...state, loadedNetworkList: action.value };
    case "SET_PEER_CHAT": {
      const next = asObject(action.value);
      const unread = { ...asObject(state.networkUnread) };
      if (next.peerId != null) delete unread[String(next.peerId)];
      return { ...state, peerChat: next, networkUnread: unread };
    }
    case "SET_PEER_CHAT_LOADED":
      return { ...state, peerChatLoaded: action.value };
    case "SET_PEER_CHAT_ACTIVE":
      return { ...state, peerChatActive: Boolean(action.value) };
    case "ADD_PEER_CHAT": {
      const incoming = asObject(action.value);
      const peerId = incoming.peer_id ?? incoming.peerId;
      const viewing = Boolean(state.peerChatActive) && peerId != null && String(state.peerChat?.peerId) === String(peerId);
      const fromId = incoming.from_id;
      const isIncoming = Boolean(incoming.is_new) && fromId != null;
      let unread = asObject(state.networkUnread);
      if (isIncoming && !viewing) {
        const key = String(fromId);
        unread = { ...unread, [key]: (Number(unread[key]) || 0) + 1 };
      }
      const existing = asArray(state.peerChat?.messages);
      const duplicate = existing.some(
        (msg) =>
          String(asObject(msg).created) === String(incoming.created) &&
          String(asObject(msg).text) === String(incoming.text) &&
          String(asObject(msg).from_id) === String(incoming.from_id)
      );
      if (!viewing) return { ...state, networkUnread: unread };
      if (duplicate) return { ...state, networkUnread: unread };
      return {
        ...state,
        networkUnread: unread,
        peerChat: {
          ...state.peerChat,
          messages: [incoming, ...existing],
        },
      };
    }
    case "CHAT_MARK_ALL_READ":
      return {
        ...state,
        chatList: {
          ...state.chatList,
          messages: asArray(state.chatList?.messages).map((msg) => ({ ...msg, is_new: false })),
        },
      };

    // Comments
    case "SET_COMMENT_LIST":
      return { ...state, comments: asList(action.value) };
    case "LOADED_COMMENT_LIST":
      return { ...state, loadedCommentList: action.value };
    case "SET_COMMENT_COUNT":
      return { ...state, commentCount: action.value };
    case "ADD_COMMENT":
      return {
        ...state,
        comments: {
          ...state.comments,
          data: [...asArray(state.comments?.data), action.value],
        },
      };
    case "EDIT_COMMENT":
      return {
        ...state,
        comments: {
          ...state.comments,
          data: asArray(state.comments?.data).map((v) => (v._id === action.value.comment_id ? { ...v, ...action.value } : v)),
        },
      };

    // Course
    case "SET_COURSE_LIST":
      return { ...state, courses: asList(action.value) };
    case "SET_COURSE_STRUCTURE":
      return { ...state, structure: { ...asObject(action.value), sections: asArray(asObject(action.value).sections) } };
    case "SET_DOWNLOAD_LIST":
      return { ...state, course_download: { ...asObject(action.value), sections: asArray(asObject(action.value).sections) } };
    case "UPDATE_COURSE_GRADE": {
      // Update question_map of the current course grade with module_id key
      const courses = [...asArray(state.courses?.data)];

      const courseIndex = courses.findIndex((c) => c.id === action.value.course_id);
      if (courseIndex < 0 || !Array.isArray(courses[courseIndex].grades)) return state;

      const gradeIndex = courses[courseIndex].grades.findIndex((g) => g.module_id === action.value.grade["module_id"]);

      if (gradeIndex < 0) {
        // Insert new question map
        const rawEarned = action.value.grade.choices ? action.value.grade.choices.filter((c) => c.correctness === "correct").length : 0;
        const rawPossible = action.value.grade.raw_possible ? Number(action.value.grade.raw_possible) : 0;

        courses[courseIndex].grades.push({
          module_id: action.value.grade["module_id"],
          section_title: action.value["sectionTitle"],
          state: {
            raw_earned: rawEarned,
            raw_possible: rawPossible < rawEarned ? rawEarned : rawPossible,
            question_map: action.value.grade["choices"],
          },
        });
      } else {
        action.value.grade.choices.forEach((choice) => {
          let state = courses[courseIndex].grades[gradeIndex].state;

          state.question_map ||= [];
          const questionIndex = state.question_map.findIndex((q) => q.id === choice.id);
          questionIndex < 0 ? state.question_map.push(choice) : (state.question_map[questionIndex] = choice);

          const rawEarned = state.question_map.filter((m) => m.correctness === "correct").length;
          const rawPossible = Number(state.raw_possible) || rawEarned;
          state.raw_earned = rawEarned;
          state.raw_possible = rawPossible < rawEarned ? rawEarned : rawPossible;

          courses[courseIndex].grades[gradeIndex].state = state;
        });
      }
      return {
        ...state,
        courses: { ...state.courses, data: courses },
      };
    }

    case "SET_MODULE_INSPECT_LIST":
      return { ...state, moduleInspects: asList(action.value) };
    case "UPDATE_MODULE_INSPECT":
      return {
        ...state,
        moduleInspects: {
          ...state.moduleInspects,
          total_spent: (state.moduleInspects.total_spent || 0) + action.value.total_spent,
          quiz_spent: (state.moduleInspects.quiz_spent || 0) + action.value.quiz_spent,
          data: asArray(state.moduleInspects?.data).find((item) => Number(item.date) === Number(currDate) && item.details.module_title === action.value.module_title)
            ? asArray(state.moduleInspects?.data).map((item) =>
                Number(item.date) === Number(currDate) && item.details.module_title === action.value.module_title
                  ? {
                      ...item,
                      details: {
                        ...item.details,
                        total_spent: item.details.total_spent + action.value.total_spent,
                        quiz_spent: item.details.quiz_spent + action.value.quiz_spent,
                        correct_count: action.value.correct_count,
                        total_count: action.value.total_count,
                      },
                    }
                  : item
              )
            : [
                {
                  date: currDate,
                  details: {
                    module_title: action.value.module_title,
                    total_spent: action.value.total_spent,
                    quiz_spent: action.value.quiz_spent,
                    correct_count: action.value.correct_count,
                    total_count: action.value.total_count,
                  },
                },
                ...state.moduleInspects.data,
              ],
        },
        courses: {
          ...state.courses,
          data: (state.courses.data || []).map((course) => (course.id === action.value.course_id ? { ...course, last_learned: new Date() } : course)),
        },
        userChart: {
          ...state.userChart,
          total_module_spent: (state.userChart.total_module_spent || 0) + (action.value.total_spent || 0),
          total_quiz_spent: (state.userChart.total_quiz_spent || 0) + (action.value.quiz_spent || 0),
          data: asArray(state.userChart?.data).find((item) => Number(item[0]) === Number(currDate))
            ? asArray(state.userChart?.data).map((item) => (Number(item[0]) === Number(currDate) ? [item[0], Number(item[1]) + action.value.total_spent, Number(item[2]) + action.value.quiz_spent, item[3], item[4]] : item))
            : [...asArray(state.userChart?.data), [Number(currDate), action.value.total_spent, action.value.quiz_spent, 0, 0]],
        },
      };
    case "SET_MEET_INSPECT_LIST":
      return { ...state, googleInspects: asList(action.value) };
    case "SET_REPLAY_INPECT_LIST":
      return { ...state, replayInspects: asList(action.value) };
    case "UPDATE_REPLAY_INSPECT": // { course_id, hash, event_id, file_id, topic, spent, instructor_name }
      return {
        ...state,
        replayInspects: {
          ...state.replayInspects,
          total_spent: (state.replayInspects.total_spent || 0) + action.value.spent,
          data: asArray(state.replayInspects?.data).find((item) => item.date === currDate && item.file_id === action.value.file_id)
            ? (state.replayInspects.data || []).map((item) =>
                item.date === currDate && item.details.file_id === action.value.file_id
                  ? {
                      ...item,
                      details: {
                        ...item.details,
                        spent: item.details.spent + action.value.spent,
                      },
                    }
                  : item
              )
            : [
                {
                  date: currDate,
                  details: {
                    file_id: action.value.file_id,
                    event_id: action.value.event_id,
                    topic: action.value.topic,
                    instructor_name: action.value.instructor_name,
                    spent: action.value.spent,
                  },
                },
                ...state.replayInspects.data,
              ],
        },
        userChart: {
          ...state.userChart,
          data: asArray(state.userChart?.data).find((item) => item[0] === currDate) ? asArray(state.userChart?.data).map((item) => (item[0] === currDate ? [item[0], item[1], item[2], Number(item[3]) + action.value.spent, item[4]] : item)) : [...asArray(state.userChart?.data), [currDate, 0, 0, action.value.spent, 0]],
        },
      };
    case "SET_USER_CHART_LIST":
      return { ...state, userChart: asObject(action.value) };
    case "SET_USER_CHART_LOADED":
      return { ...state, loadedUserChartList: action.value };

    // Loading
    case "SET_CHAT_LIST_LOADED":
      return { ...state, chats_loaded: action.value };
    case "SET_CHAT_MORE_LIST_LOADED":
      return { ...state, chats_more_loaded: action.value };
    case "LOADED_APP_DASHBOARD":
      return { ...state, loadedDashboard: action.value };
    case "LOADED_APP_COURSE_DETAIL_GET":
      return { ...state, loadedCourseDetail: action.value };
    case "LOADED_APP_COURSE_UNIT_GET":
      return { ...state, loadedCourseUnit: action.value };
    case "LOADED_LOGIN_HIST_LIST":
      return { ...state, loadedLoginHistList: action.value };
    case "LOADED_MODULE_INSPECT_LIST":
      return { ...state, loadedModuleInspectList: action.value };
    case "LOADED_GOOGLE_INSPECT_LIST":
      return { ...state, loadedGoogleInspectList: action.value };
    case "LOADED_REPLAY_INSPECT_LIST":
      return { ...state, loadedReplayInspectList: action.value };

    case "SET_PREFERENCE":
      return { ...state, preference: asObject(action.value) };
    case "SET_REVIEW":
      return { ...state, review: asObject(action.value) };
    case "SET_TERM":
      return { ...state, term: asObject(action.value) };
    case "SET_THEME_LIST":
      return { ...state, themes: asArray(action.value) };
    case "SET_ROUTER_TRACK":
      return { ...state, trackRoutes: asArray(action.value) };
    case "UPDATE_ROUTER_TRACK":
      return {
        ...state,
        trackRoutes: asArray(state.trackRoutes).find((v) => v.course_id === action.value.course_id) ? asArray(state.trackRoutes).map((v) => (v.course_id === action.value.course_id ? { ...v, url: action.value.url } : v)) : [...asArray(state.trackRoutes), action.value],
      };
    case "SET_VIEWED_LIST":
      return { ...state, viewedVerticals: [...new Set([...asArray(state.viewedVerticals), ...asArray(action.value)])] };
    case "UPDATE_VIEWED":
      return { ...state, viewedVerticals: [...new Set([...asArray(state.viewedVerticals), action.value])] };
    case "SET_CURRENT_VIMEO_SEEK":
      return { ...state, currVimeo: action.value };
    case "SET_XBLOCK":
      return { ...state, xblock: asArray(action.value) };

    case "SET_UPCOMING_EVENT_LIST":
      return { ...state, upcomingSessions: asList(action.value) };
    case "SET_RECORDING_LIST":
      return { ...state, pastSessions: asList(action.value) };
    case "SET_PAST_SESSION_LOADED":
      return { ...state, loadedPastSession: action.value };
    case "SET_NOTIFICATION_LIST":
      return { ...state, notifications: asArray(action.value) };
    case "SET_PREVIEW_MODE":
      return { ...state, preview: action.value };
    case "SET_MEET_EVENT_LIST":
      return { ...state, meetEvents: asList(action.value) };
    case "SET_TRACK_DISABLED":
      return { ...state, trackDisabled: action.value };
    case "HYDRATE_SESSION":
      return {
        ...state,
        ...asObject(action.value),
        sessionHydrated: true,
        restoredFromSession: true,
      };
    case "SET_SESSION_HYDRATED":
      return { ...state, sessionHydrated: true };
    case "SET_FIRST_LOAD_PENDING":
      return { ...state, firstLoadPending: !!action.value };

    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

/**
 * Provides the learner app store to descendant components.
 *
 * @param {{ children: React.ReactNode }} props
 */
const AsterControllerProvider = ({ children }) => {
  const initialState = {
    miniSidenav: false,
    headerSidenav: false,
    mobile: false,
    darkMode: false,
    loadedDashboardGet: false,
    trackDisabled: false,

    admin: {},
    isEditProfileAlert: false,
    groups: [],

    user: {},
    loginHists: PAGE_DEFAULT,
    isProfileUpdateLoading: true,
    isProfileUpdateSuccess: true,

    categories: PAGE_DEFAULT,
    certificates: PAGE_DEFAULT,
    certVar: {},

    chatList: { message_more: false, page: 0, messages: [] },
    isChatEnabled: true,
    chatTyping: { typing: false },
    networkContacts: [],
    networkPresence: {},
    loadedNetworkList: true,
    networkUnread: {},
    peerChat: { peerId: null, message_more: false, page: 0, messages: [] },
    peerChatLoaded: true,
    peerChatActive: false,

    comments: PAGE_DEFAULT,
    loadedCommentList: true,
    commentCount: 0,

    courses: PAGE_DEFAULT,
    structure: { sections: [] },
    course_download: { sections: [] },

    moduleInspects: PAGE_DEFAULT,
    googleInspects: PAGE_DEFAULT,
    replayInspects: PAGE_DEFAULT,

    loadedDashboard: false,
    loadedCourseDetail: false,
    loadedCourseUnit: false,
    loadedModuleInspectList: false,
    loadedGoogleInspectList: false,
    loadedReplayInspectList: false,
    loadedLoginHistList: false,
    chats_loaded: false,
    chats_more_loaded: true,

    preference: {},
    review: { type: null, content: null },
    term: {},
    themes: [],
    trackRoutes: [], // { course_id, url }
    viewedVerticals: [],
    currVimeo: { vimeo_id: null, seek: 0 },
    xblock: [],
    upcomingSessions: PAGE_DEFAULT,
    pastSessions: PAGE_DEFAULT,
    userChart: { data: [] },
    loadedUserChartList: true,

    notifications: [],
    preview: null,
    meetEvents: PAGE_DEFAULT,
    sessionHydrated: false,
    restoredFromSession: false,
    firstLoadPending: true,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);
  const controllerRef = useRef(controller);
  controllerRef.current = controller;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const snap = await loadSession();
      if (cancelled) return;
      if (snap?.state) {
        setPreferSession(true);
        if (snap.path) writeLastPath(snap.path);
        dispatch({ type: "HYDRATE_SESSION", value: snap.state });
        return;
      }
      dispatch({ type: "SET_SESSION_HYDRATED" });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const persistNow = () => {
      if (!controllerRef.current.sessionHydrated) return;
      saveSession(controllerRef.current, readLastPath());
    };
    window.addEventListener("pagehide", persistNow);
    window.addEventListener("beforeunload", persistNow);
    return () => {
      window.removeEventListener("pagehide", persistNow);
      window.removeEventListener("beforeunload", persistNow);
    };
  }, []);

  useEffect(() => {
    if (!controller.sessionHydrated) return;
    const timer = setTimeout(() => {
      saveSession(controller, readLastPath());
    }, 250);
    return () => clearTimeout(timer);
  }, [controller]);
  return <AsterContext.Provider value={[controller, dispatch]}>{children}</AsterContext.Provider>;
};

AsterControllerProvider.propTypes = {
  children: PropTypes.any.isRequired,
};

/**
 * Returns `[controller, dispatch]` from the nearest Aster store provider.
 *
 * @returns {[Object, Function]}
 */
const useAsterController = () => {
  const context = useContext(AsterContext);

  if (!context) {
    throw new Error("[useAsterController should be used inside the AsterControllerProvider.]");
  }

  return context;
};

/**
 * Tracks viewport width and height.
 *
 * @returns {{ width: number|undefined, height: number|undefined }}
 */
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    /**
     * Syncs window size state from the current viewport.
     *
     * @returns {void}
     */
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    // Add event listener
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowSize;
};

export { AsterControllerProvider, useAsterController, useWindowSize };
